import api, { withLoading } from '@/api/api';
import parseParams from "@/helpers/objectToQueryParams";

/**
 * Representa um Business Plan completo retornado por show/create/update
 * 
 * @typedef {Object} BusinessPlan
 * @property {number} id - Identificador único
 * @property {number} usuario_id - ID do usuário criador
 * @property {string} nome - Nome do Business Plan
 * @property {string} iniciativa_escopo - Escopo da iniciativa
 * @property {string} iniciativa_escopo_solucao - Solução da iniciativa
 * @property {string} iniciativa_inovacoes - Inovações da iniciativa
 * @property {string} iniciativa_riscos_barreiras - Riscos e barreiras da iniciativa
 * @property {string} iniciativa_relacoes_tecnologia - Relações com tecnologia
 * @property {string} iniciativa_artefato_tecnologico - Artefato tecnológico
 * @property {string} ativo_controle - Controle do ativo
 * @property {string} kpi - Indicador de desempenho
 * @property {number} impacto_financeiro - Impacto financeiro
 * @property {string} impacto_financeiro_label - Label do impacto financeiro
 * @property {string} ativo_moeda_caixa - Moeda/caixa do ativo
 * @property {string} ativo_destinado_uso - Destinação de uso
 * @property {string} ativo_forma_separacao - Forma de separação
 * @property {string} ativo_substancia_fisica - Substância física
 * @property {string} ativo_produto_servico_semelhante - Produto/serviço semelhante
 * @property {string} ativo_produto_servico_semelhante_desc - Descrição do produto/serviço semelhante
 * @property {string} mercado_comercializacao - Mercado de comercialização
 * @property {string} potenciais_consumidores - Potenciais consumidores
 * @property {string} engenharia_valor - Engenharia de valor
 * @property {boolean} non_people - Indica se não envolve pessoas
 * @property {string} relacao_id_epicos_portifolios - Relação com épicos/portfólios
 * @property {string} responsaveis_participantes_registro_horas - Registro de horas dos responsáveis/participantes
 * @property {string} responsavel_tecnico - Responsável técnico
 * @property {string} lider_tecnologia - Líder de tecnologia
 * @property {string} lider_produto - Líder de produto
 * @property {string} lider_negocio - Líder de negócio
 * @property {number} empresa_beneficiada - Empresa beneficiada
 * @property {string} empresa_beneficiada_label - Label da empresa beneficiada
 * @property {string} retorno_financeiro - Retorno financeiro
 * @property {number} tracking - Tracking
 * @property {string} tracking_label - Label do tracking
 * @property {string} tracking_detalhamento - Detalhamento do tracking
 * @property {string} vida_util - Vida útil
 * @property {string} vida_util_justificativa - Justificativa da vida útil
 * @property {number[]} business_unit_ids - IDs das business units
 * @property {Array} business_units - Business units vinculadas
 * @property {number} status - Status do Business Plan
 * @property {string} status_label - Label do status
 * @property {string|null} data_inicio - Data de início no formato "Y-m-d"
 * @property {string|null} data_fim - Data de fim no formato "Y-m-d"
 * @property {number|null} workflow_id - ID do workflow
 * @property {string|null} workflow_data_expiracao - Data de expiração do workflow
 * @property {string|null} workflow_nome - Nome do workflow
 * @property {Object|null} workflow - Objeto do workflow
 * @property {string|null} sla_label - Label do SLA
 * @property {Array} responsaveis - Responsáveis vinculados
 * @property {Array} responsaveis_acompanhamento - Responsáveis de acompanhamento
 * @property {BusinessPlanAnexo[]} anexos - Anexos do Business Plan
 * @property {ParticipanteVinculado[]} participantes - Participantes vinculados
 * @property {TipoRubricaVpl[]} rubricas - Rubricas VPL
 * @property {Array} versoes - Versões do Business Plan
 * @property {boolean} editavel - Se o Business Plan é editável
 * @property {Array} comentarios - Comentários do Business Plan
 */

/**
 * Representa um item da listagem paginada de Business Plans
 * 
 * @typedef {Object} BusinessPlanIndex
 * @property {number} id - Identificador único
 * @property {number} usuario_id - ID do usuário criador
 * @property {string} nome - Nome do Business Plan
 * @property {string} status - Status do Business Plan
 * @property {string} status_label - Label do status
 * @property {string|null} data_inicio - Data de início no formato "Y-m-d"
 * @property {string|null} data_fim - Data de fim no formato "Y-m-d"
 * @property {number|null} workflow_id - ID do workflow
 * @property {number|null} workflow_status_id - ID do status do workflow
 * @property {string|null} workflow_data_expiracao - Data de expiração do workflow
 * @property {string|null} workflow_nome - Nome do workflow
 * @property {*|null} sla - SLA
 * @property {string|null} sla_label - Label do SLA
 */

/**
 * Representa o payload de criação/atualização de um Business Plan
 * 
 * @typedef {Object} BusinessPlanPayload
 * @property {string} nome - Nome do Business Plan (obrigatório, max 255, único por empresa)
 * @property {string} [iniciativa_escopo] - Escopo da iniciativa
 * @property {string} [iniciativa_escopo_solucao] - Solução da iniciativa
 * @property {string} [iniciativa_inovacoes] - Inovações da iniciativa
 * @property {string} [iniciativa_riscos_barreiras] - Riscos e barreiras
 * @property {string} [iniciativa_relacoes_tecnologia] - Relações com tecnologia
 * @property {number[]} [business_unit_ids] - IDs das business units
 * @property {{id: number}} [lider_tecnologia] - Líder de tecnologia
 * @property {{id: number}} [lider_produto] - Líder de produto
 * @property {{id: number}} [lider_negocio] - Líder de negócio
 * @property {Array<{id: number}>} [responsaveis_acompanhamento] - Responsáveis de acompanhamento
 */

/**
 * Representa a resposta do endpoint de status de Business Plan
 * 
 * @typedef {Object} BusinessPlanStatus
 * @property {number} id - Identificador único
 * @property {string} status - Status
 * @property {string} label - Label do status
 */

/**
 * Representa um item da seleção de Business Plans
 * 
 * @typedef {Object} BusinessPlanSelecao
 * @property {number} id - Identificador único
 * @property {string} nome - Nome do Business Plan
 * @property {string|null} data_inicio - Data de início no formato "Y-m-d"
 * @property {string|null} data_fim - Data de fim no formato "Y-m-d"
 * @property {string} status - Status
 * @property {string} versao - Versão
 * @property {boolean} versao_oficial - Se é versão oficial
 */

/**
 * Representa um participante vinculado a um Business Plan
 * 
 * @typedef {Object} ParticipanteVinculado
 * @property {number} id - Identificador único
 * @property {string} nome - Nome do participante
 */

/**
 * Representa um anexo de Business Plan
 * 
 * @typedef {Object} BusinessPlanAnexo
 * @property {number} id - Identificador único
 * @property {number} business_plan_id - ID do Business Plan
 * @property {string} nome - Nome do anexo
 * @property {string} url - URL do anexo
 * @property {string|null} tipo - Tipo do anexo
 * @property {string|null} criado_em - Data de criação
 * @property {string|null} modificado_em - Data da modificação
 */

/**
 * Representa um tipo de rubrica VPL
 * 
 * @typedef {Object} TipoRubricaVpl
 * @property {number} id - Identificador único
 * @property {string} descricao - Descrição
 * @property {number} tipo_id - Tipo (1=Retorno Financeiro|2=Custo)
 * @property {string} tipo - Label do tipo
 */

const resource = {
  base: (empresaId) => `v1/ativacaoped/${empresaId}/business-plans`,
  businessPlan: (empresaId, businessPlanId) => `v1/ativacaoped/${empresaId}/business-plans/${businessPlanId}`
};

const LOADING_KEY = 'ativacaoped-business-plan';

/**
 * Lista Business Plans com paginação e filtros.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {Object} [params] - Parâmetros de consulta
 * @param {number} [params.page] - Número da página
 * @param {number} [params.per_page] - Itens por página
 * @returns {Promise<PaginacaoLaravelResource<BusinessPlanIndex>>} Lista paginada de Business Plans
 */
export const getBusinessPlans = (empresaId, params = { page: 1, per_page: 10 }) => {
  const query = parseParams(params);
  let url = resource.base(empresaId);

  if (query) {
    url = url.concat(`?${query}`);
  }

  return withLoading(LOADING_KEY, () =>
    api.get(url).then(({ data }) => data)
  );
};

/**
 * Obtém um Business Plan específico pelo ID.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {number|string} businessPlanId - ID do Business Plan
 * @returns {Promise<BusinessPlan>} Dados do Business Plan
 */
export const getBusinessPlan = (empresaId, businessPlanId) => {
  const baseUrl = resource.businessPlan(empresaId, businessPlanId);

  return withLoading(LOADING_KEY, () =>
    api.get(baseUrl).then(({ data }) => data.data)
  );
};

/**
 * Lista Business Plans simplificados para componentes de seleção (dropdown, autocomplete).
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {Object} [params] - Parâmetros de consulta
 * @returns {Promise<BusinessPlanSelecao[]>} Lista de Business Plans
 */
export const getSelecaoBusinessPlans = (empresaId, params = {}) => {
  const query = parseParams(params);
  let url = resource.base(empresaId).concat(`/selecao`);

  if (query) {
    url = url.concat(`?${query}`);
  }

  return withLoading(LOADING_KEY, () =>
    api.get(url).then(({ data }) => data.data || data)
  );
};

/**
 * Lista demandantes (usuários) para seleção.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {Object} [params] - Parâmetros de consulta
 * @returns {Promise<Array<{id: number, nome: string}>>} Lista de demandantes
 */
export const getSelecaoDemandantes = (empresaId, params = {}) => {
  const query = parseParams(params);
  let url = resource.base(empresaId).concat(`/selecao-demandantes`);

  if (query) {
    url = url.concat(`?${query}`);
  }

  return withLoading(LOADING_KEY, () =>
    api.get(url).then(({ data }) => data.data || data)
  );
};

/**
 * Lista os status possíveis de Business Plan.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {Object} [params] - Parâmetros de consulta
 * @returns {Promise<BusinessPlanStatus[]>} Lista de status
 */
export const getBusinessPlanStatus = (empresaId, params = {}) => {
  const query = parseParams(params);
  let url = resource.base(empresaId).concat(`/status`);

  if (query) {
    url = url.concat(`?${query}`);
  }

  return withLoading(LOADING_KEY, () =>
    api.get(url).then(({ data }) => data.data || data)
  );
};

/**
 * Obtém as rubricas VPL de um Business Plan.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {number|string} businessPlanId - ID do Business Plan
 * @returns {Promise<TipoRubricaVpl[]>} Lista de rubricas
 */
export const getBusinessPlanRubricas = (empresaId, businessPlanId) => {
  const baseUrl = resource.businessPlan(empresaId, businessPlanId).concat(`/rubricas`);

  return withLoading(LOADING_KEY, () =>
    api.get(baseUrl).then(({ data }) => data.data || data)
  );
};

/**
 * Cria um novo Business Plan.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {BusinessPlanPayload} data - Dados do Business Plan
 * @returns {Promise<BusinessPlan>} Business Plan criado
 */
export const postBusinessPlan = (empresaId, data) => {
  const baseUrl = resource.base(empresaId);

  return withLoading(LOADING_KEY, () =>
    api.post(baseUrl, data).then(({ data }) => data.data || data)
  );
};

/**
 * Atualiza um Business Plan existente (backend usa POST para update).
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {number|string} businessPlanId - ID do Business Plan
 * @param {BusinessPlanPayload} data - Dados parciais para atualização
 * @returns {Promise<BusinessPlan>} Business Plan atualizado
 */
export const putBusinessPlan = (empresaId, businessPlanId, data) => {
  const baseUrl = resource.businessPlan(empresaId, businessPlanId);

  return withLoading(LOADING_KEY, () =>
    api.post(baseUrl, data).then(({ data }) => data.data || data)
  );
};

/**
 * Exclui um Business Plan.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {number|string} businessPlanId - ID do Business Plan a ser excluído
 * @returns {Promise<void>}
 */
export const deleteBusinessPlan = (empresaId, businessPlanId) => {
  const baseUrl = resource.businessPlan(empresaId, businessPlanId);

  return withLoading(LOADING_KEY, () =>
    api.delete(baseUrl).then(({ data }) => data)
  );
};

/**
 * Importa Business Plans via arquivo (multipart: `arquivo` file .xlsx + `tipo`).
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {FormData} formData - FormData com `arquivo` e `tipo`
 * @param {string} formData.tipo - Tipo de importação (business-plan|timesheet|responsaveis|participantes|centros-de-custo|consolidados|vpl-consolidado)
 * @returns {Promise<Object>} Resultado da importação
 */
export const importarBusinessPlans = (empresaId, formData) => {
  const baseUrl = resource.base(empresaId).concat(`/importar`);

  return withLoading(LOADING_KEY, () =>
    api.post(baseUrl, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }).then(({ data }) => data)
  );
};

/**
 * Gera uma nova versão do Business Plan.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {number|string} businessPlanId - ID do Business Plan
 * @returns {Promise<BusinessPlan>} Nova versão criada
 */
export const postGerarVersao = (empresaId, businessPlanId) => {
  const baseUrl = resource.businessPlan(empresaId, businessPlanId).concat(`/versao`);

  return withLoading(LOADING_KEY, () =>
    api.post(baseUrl).then(({ data }) => data.data || data)
  );
};

/**
 * Aprova a versão do Business Plan.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {number|string} businessPlanId - ID do Business Plan
 * @returns {Promise<Object>} Resultado da aprovação
 */
export const postAprovarVersao = (empresaId, businessPlanId) => {
  const baseUrl = resource.businessPlan(empresaId, businessPlanId).concat(`/aprovar-versao`);

  return withLoading(LOADING_KEY, () =>
    api.post(baseUrl).then(({ data }) => data)
  );
};

/**
 * Reprova a versão do Business Plan.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {number|string} businessPlanId - ID do Business Plan
 * @returns {Promise<Object>} Resultado da reprovação
 */
export const postReprovarVersao = (empresaId, businessPlanId) => {
  const baseUrl = resource.businessPlan(empresaId, businessPlanId).concat(`/reprovar-versao`);

  return withLoading(LOADING_KEY, () =>
    api.post(baseUrl).then(({ data }) => data)
  );
};

/**
 * Notifica sobre nova versão do Business Plan.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {number|string} businessPlanId - ID do Business Plan
 * @returns {Promise<Object>} Resultado da notificação
 */
export const postNotificarNovaVersao = (empresaId, businessPlanId) => {
  const baseUrl = resource.businessPlan(empresaId, businessPlanId).concat(`/notificar-nova-versao`);

  return withLoading(LOADING_KEY, () =>
    api.post(baseUrl).then(({ data }) => data)
  );
};

/**
 * Atualiza o status de vários Business Plans em lote.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {Object} data - Dados do lote
 * @param {number[]} data.business_plan_ids - IDs dos Business Plans
 * @param {number} data.status - Status a aplicar
 * @returns {Promise<Object>} Resultado da atualização
 */
export const putStatusBusinessPlansBatch = (empresaId, data) => {
  const baseUrl = resource.base(empresaId).concat(`/status`);

  return withLoading(LOADING_KEY, () =>
    api.post(baseUrl, data).then(({ data }) => data)
  );
};

/**
 * Atualiza os responsáveis de acompanhamento de vários Business Plans em lote.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {Object} data - Dados do lote
 * @param {Array} data.business_plans - Lista de Business Plans
 * @param {Array} data.responsaveis - Lista de responsáveis
 * @returns {Promise<Object>} Resultado da atualização
 */
export const putResponsaveisAcompanhamentoBatch = (empresaId, data) => {
  const baseUrl = resource.base(empresaId).concat(`/responsaveis-acompanhamento`);

  return withLoading(LOADING_KEY, () =>
    api.post(baseUrl, data).then(({ data }) => data)
  );
};

/**
 * Lista timesheets de um Business Plan com paginação.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {number|string} businessPlanId - ID do Business Plan
 * @param {Object} [params] - Parâmetros de consulta
 * @returns {Promise<PaginacaoLaravelResource<Object>>} Lista paginada de timesheets
 */
export const getTimesheets = (empresaId, businessPlanId, params = {}) => {
  const query = typeof params === "string" ? params : parseParams(params);
  let url = resource.businessPlan(empresaId, businessPlanId).concat(`/timesheets`);

  if (query) {
    url = url.concat(`?${query}`);
  }

  return withLoading(LOADING_KEY, () =>
    api.get(url).then(({ data }) => data)
  );
};

/**
 * Obtém um timesheet específico de um Business Plan.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {number|string} businessPlanId - ID do Business Plan
 * @param {number|string} timesheetId - ID do timesheet
 * @returns {Promise<Object>} Dados do timesheet
 */
export const getTimesheet = (empresaId, businessPlanId, timesheetId) => {
  const baseUrl = resource.businessPlan(empresaId, businessPlanId).concat(`/timesheets/${timesheetId}`);

  return withLoading(LOADING_KEY, () =>
    api.get(baseUrl).then(({ data }) => data.data || data)
  );
};

/**
 * Cria timesheets para um Business Plan.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {number|string} businessPlanId - ID do Business Plan
 * @param {Object} data - Dados dos timesheets
 * @param {Array<{participante_id: number, competencia: string, percentual: number}>} data.realizados - Lista de realizados
 * @returns {Promise<Object>} Resultado da criação
 */
export const postTimesheet = (empresaId, businessPlanId, data) => {
  const baseUrl = resource.businessPlan(empresaId, businessPlanId).concat(`/timesheets`);

  return withLoading(LOADING_KEY, () =>
    api.post(baseUrl, data).then(({ data }) => data)
  );
};

/**
 * Exclui timesheets de um Business Plan em lote.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {number|string} businessPlanId - ID do Business Plan
 * @param {number[]} ids - Lista de IDs dos timesheets a excluir
 * @returns {Promise<void>}
 */
export const deleteTimesheets = (empresaId, businessPlanId, ids) => {
  const baseUrl = resource.businessPlan(empresaId, businessPlanId).concat(`/timesheets`);

  return withLoading(LOADING_KEY, () =>
    api.delete(baseUrl, {
      data: { ids }
    }).then(({ data }) => data)
  );
};

/**
 * Exclui um timesheet específico de um Business Plan.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {number|string} businessPlanId - ID do Business Plan
 * @param {number|string} timesheetId - ID do timesheet a excluir
 * @returns {Promise<void>}
 */
export const deleteTimesheet = (empresaId, businessPlanId, timesheetId) => {
  const baseUrl = resource.businessPlan(empresaId, businessPlanId).concat(`/timesheets/${timesheetId}`);

  return withLoading(LOADING_KEY, () =>
    api.delete(baseUrl).then(({ data }) => data)
  );
};

export default {
  getBusinessPlan,
  getBusinessPlans,
  getBusinessPlanRubricas,
  getBusinessPlanStatus,
  getSelecaoBusinessPlans,
  getSelecaoDemandantes,
  getTimesheet,
  getTimesheets,
  postBusinessPlan,
  putBusinessPlan,
  deleteBusinessPlan,
  importarBusinessPlans,
  postGerarVersao,
  postAprovarVersao,
  postReprovarVersao,
  postNotificarNovaVersao,
  putStatusBusinessPlansBatch,
  putResponsaveisAcompanhamentoBatch,
  postTimesheet,
  deleteTimesheets,
  deleteTimesheet
};